/** Small, pure helpers for the SVG charts: nice axis ticks, linear scales, downsampling, bar paths. */

export interface NiceScale {
  /** Upper end of the axis (a multiple of `step`). */
  max: number;
  step: number;
  ticks: number[];
}

/**
 * Round axis for values from 0 to `value`: a step of 1, 2 or 5 times a power of ten, and the
 * smallest multiple of it that covers `value`. `integer` keeps the step at 1 or more (counts).
 */
export function niceScale(value: number, targetTicks = 4, integer = false): NiceScale {
  const safe = Number.isFinite(value) && value > 0 ? value : 1;
  const raw = safe / Math.max(1, targetTicks);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const residual = raw / magnitude;
  const factor = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
  let step = factor * magnitude;
  if (integer) step = Math.max(1, step);
  const max = Math.ceil(safe / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let t = 0; t <= max + step / 2; t += step) ticks.push(Math.round(t * 1e6) / 1e6);
  return { max, step, ticks };
}

/** Round ticks from 0 up to (and not beyond) `max`, for an axis that ends exactly at `max` (turn numbers). */
export function tickValues(max: number, targetTicks = 5): number[] {
  if (!(max > 0)) return [0];
  const { step } = niceScale(max, targetTicks, true);
  const ticks: number[] = [];
  for (let t = 0; t <= max; t += step) ticks.push(t);
  return ticks;
}

/** Maps `[d0, d1]` linearly to `[r0, r1]`. A zero-width domain maps to the middle of the range. */
export function linearScale(d0: number, d1: number, r0: number, r1: number): (v: number) => number {
  if (d1 === d0) return () => (r0 + r1) / 2;
  const k = (r1 - r0) / (d1 - d0);
  return (v) => r0 + (v - d0) * k;
}

/** Keeps at most `max` items, evenly spread, always including the first and the last. */
export function downsample<T>(items: T[], max: number): T[] {
  if (items.length <= max || max < 2) return items;
  const out: T[] = [];
  const last = items.length - 1;
  for (let i = 0; i < max; i++) out.push(items[Math.round((i * last) / (max - 1))]!);
  return out;
}

/** Path of a bar with rounded top corners and a square baseline end. */
export function roundedTopBar(x: number, y: number, w: number, h: number, radius: number): string {
  if (h <= 0) return "";
  const r = Math.min(radius, w / 2, h);
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

/** SVG path for a polyline through the given points. */
export function linePath(points: { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${round(p.x)},${round(p.y)}`).join(" ");
}

function round(n: number): number {
  return Math.round(n * 10) / 10;
}
